/**
 * Utility functions for detecting track variants (Live, Acoustic, Remix, etc.)
 * and determining which track is the "original" vs a variant
 */

/**
 * Common track variant patterns
 * These patterns indicate a track is a variant/version of another track
 */
const VARIANT_PATTERNS = [
  // Live versions
  /[-–—]\s*live(?:\s+version)?(?:\s+\d{4})?/gi,
  /\(\s*live(?:\s+version)?(?:\s+\d{4})?\s*\)/gi,
  /\[\s*live(?:\s+version)?(?:\s+\d{4})?\s*\]/gi,

  // Acoustic versions
  /[-–—]\s*acoustic(?:\s+version)?/gi,
  /\(\s*acoustic(?:\s+version)?\s*\)/gi,
  /\[\s*acoustic(?:\s+version)?\s*\]/gi,

  // Remixes
  /[-–—]\s*remix/gi,
  /\(\s*remix\s*\)/gi,
  /\[\s*remix\s*\]/gi,
  /[-–—]\s*\w+(?:\s+\w+)?\s+remix/gi, // "- Artist Remix", "- Artist Name Remix"
  /\(\s*\w+(?:\s+\w+)?\s+remix\s*\)/gi, // "(Artist Remix)"

  // Redux versions (matches any number of words before Redux)
  /[-–—]\s*(?:[\w']+\s+)*redux/gi, // "- Redux", "- Modifier Redux", "- Multi Word Title Redux"
  /\(\s*(?:[\w']+\s+)*redux\s*\)/gi, // "(Redux)", "(Modifier Redux)"
  /\[\s*(?:[\w']+\s+)*redux\s*\]/gi, // "[Redux]", "[Modifier Redux]"

  // Mixes
  /[-–—]\s*\w+(?:\s+\w+)?\s+mix/gi, // "- Extended Mix", "- Radio Mix"
  /\(\s*\w+(?:\s+\w+)?\s+mix\s*\)/gi, // "(Extended Mix)"
  /\[\s*\w+(?:\s+\w+)?\s+mix\s*\]/gi, // "[Radio Mix]"

  // Remaster versions
  /[-–—]\s*\d{4}\s*remaster(?:ed)?/gi,
  /[-–—]\s*remaster(?:ed)?(?:\s+\d{4})?/gi,
  /\(\s*\d{4}\s*remaster(?:ed)?\s*\)/gi,
  /\(\s*remaster(?:ed)?(?:\s+\d{4})?\s*\)/gi,
  /\bremaster(?:ed)?\b/gi,

  // Radio/Extended edits
  /[-–—]\s*radio\s+edit/gi,
  /\(\s*radio\s+edit\s*\)/gi,
  /[-–—]\s*extended(?:\s+version)?/gi,
  /\(\s*extended(?:\s+version)?\s*\)/gi,

  // Instrumental versions
  /[-–—]\s*instrumental/gi,
  /\(\s*instrumental\s*\)/gi,
  /\[\s*instrumental\s*\]/gi,

  // Demo versions
  /[-–—]\s*(?:\w+\s+)?demo(?:\s+version)?/gi, // "- Demo", "- Quiet Demo", "- Home Demo"
  /\(\s*(?:\w+\s+)?demo(?:\s+version)?\s*\)/gi, // "(Demo)", "(Quiet Demo)"
  /\[\s*(?:\w+\s+)?demo(?:\s+version)?\s*\]/gi, // "[Demo]", "[Early Demo]"

  // Other common variants
  /[-–—]\s*unplugged/gi,
  /\(\s*unplugged\s*\)/gi,
  /[-–—]\s*single(?:\s+version)?/gi,
  /\(\s*single(?:\s+version)?\s*\)/gi,
  /[-–—]\s*album(?:\s+version)?/gi,
  /\(\s*album(?:\s+version)?\s*\)/gi,
  /[-–—]\s*explicit/gi,
  /\(\s*explicit\s*\)/gi,
  /[-–—]\s*clean/gi,
  /\(\s*clean\s*\)/gi,
];

/**
 * Patterns for collaborator/featuring info
 * These are NOT variant patterns but should be stripped for comparison to identify
 * deluxe edition tracks where the same song appears with additional featuring artists
 */
const COLLABORATOR_PATTERNS = [
  // Featuring patterns - matches the entire featuring section including all listed artists
  /[-–—]\s*(?:feat|ft|featuring|with)[.\s][^[\]()]+$/gi, // Dash format at end of title
  /\(\s*(?:feat|ft|featuring|with)[.\s][^)]+\)/gi, // Parentheses format
  /\[\s*(?:feat|ft|featuring|with)[.\s][^\]]+\]/gi, // Bracket format
];

/**
 * Check if a track title contains variant indicators
 */
export function isTrackVariant(title: string): boolean {
  if (!title || typeof title !== "string")
    return false;

  return VARIANT_PATTERNS.some(pattern => pattern.test(title));
}

/**
 * Remove variant suffixes and collaborator info from a title to get the "base" title
 * Used for comparing titles to find matches
 * Strips: Live, Acoustic, Remix, Redux, feat/featuring info, etc.
 */
export function stripVariantSuffix(title: string): string {
  if (!title || typeof title !== "string")
    return "";

  let stripped = title;

  // First, remove collaborator/featuring info
  for (const pattern of COLLABORATOR_PATTERNS) {
    stripped = stripped.replace(pattern, "");
  }

  // Then, apply all variant patterns to remove variant suffixes
  for (const pattern of VARIANT_PATTERNS) {
    stripped = stripped.replace(pattern, "");
  }

  // Clean up any leftover punctuation/whitespace
  stripped = stripped
    .replace(/[-–—]\s*$/g, "") // Remove trailing dashes
    .replace(/\(\s*\)$/g, "") // Remove empty parentheses
    .replace(/\[\s*\]$/g, "") // Remove empty brackets
    .replace(/\s+/g, " ") // Normalize whitespace
    .trim();

  return stripped;
}

/**
 * Determine which of two tracks is the "original" and which is the variant
 * Returns: { originalId, variantId, confidence }
 */
export function determineOriginalVsVariant(
  track1: { id: string; name: string; track_number?: number },
  track2: { id: string; name: string; track_number?: number },
): {
  originalId: string;
  variantId: string;
  confidence: "high" | "medium" | "low";
} | null {
  // Case 0: Prefix matching - if one title is a complete prefix of the other
  // The shorter title is considered the original, the longer title is the variant
  // This handles cases where additional text is appended to the original title
  const lower1 = track1.name.toLowerCase().trim();
  const lower2 = track2.name.toLowerCase().trim();

  if (lower1 !== lower2) {
    // Check if track2 starts with track1 (track1 is shorter/prefix)
    if (lower2.startsWith(lower1)) {
      return {
        originalId: track1.id,
        variantId: track2.id,
        confidence: "high",
      };
    }
    // Check if track1 starts with track2 (track2 is shorter/prefix)
    if (lower1.startsWith(lower2)) {
      return {
        originalId: track2.id,
        variantId: track1.id,
        confidence: "high",
      };
    }
  }

  const track1IsVariant = isTrackVariant(track1.name);
  const track2IsVariant = isTrackVariant(track2.name);

  // Case 1: Only one is a variant - that's the variant, other is original
  if (track1IsVariant && !track2IsVariant) {
    return {
      originalId: track2.id,
      variantId: track1.id,
      confidence: "high",
    };
  }
  if (track2IsVariant && !track1IsVariant) {
    return {
      originalId: track1.id,
      variantId: track2.id,
      confidence: "high",
    };
  }

  // Case 2: Both are variants or neither are variants
  // Use track number heuristic - lower track number is typically the original
  if (track1.track_number != null && track2.track_number != null) {
    if (track1.track_number < track2.track_number) {
      return {
        originalId: track1.id,
        variantId: track2.id,
        confidence: track1IsVariant && track2IsVariant ? "medium" : "low",
      };
    }
    return {
      originalId: track2.id,
      variantId: track1.id,
      confidence: track1IsVariant && track2IsVariant ? "medium" : "low",
    };
  }

  // Case 3: Can't determine
  return null;
}

/**
 * Check if two track titles are similar after removing variant suffixes
 * This is used alongside string similarity algorithms
 */
export function areTracksLikelyVariants(title1: string, title2: string): boolean {
  if (!title1 || !title2 || typeof title1 !== "string" || typeof title2 !== "string")
    return false;

  const lower1 = title1.toLowerCase().trim();
  const lower2 = title2.toLowerCase().trim();

  // Check if one title is a prefix of the other
  const isPrefix = lower1.startsWith(lower2) || lower2.startsWith(lower1);

  if (isPrefix) {
    // If prefix match exists, check if the longer title has variant patterns
    // Examples:
    // - "Title" vs "Title - Live" → VARIANT (has Live pattern)
    // - "Title" vs "Title - Artist Name Redux" → NOT VARIANT (Redux is part of collaborator name)
    const longerTitle = lower1.length > lower2.length ? title1 : title2;
    const shorterTitle = lower1.length > lower2.length ? title2 : title1;

    // Check if longer title has variant patterns
    if (isTrackVariant(longerTitle)) {
      // The longer title contains a variant pattern (Live, Acoustic, Redux, etc.)
      return true;
    }

    // Check if it's ONLY collaborator/feature info (not a variant)
    // If stripping the shorter title from the longer one leaves ONLY feat/collaborator info, NOT a variant
    const longerLower = longerTitle.toLowerCase().trim();
    const shorterLower = shorterTitle.toLowerCase().trim();
    const remainder = longerLower.substring(shorterLower.length).trim();

    // If remainder starts with common separators and feat/featuring, check if it's ONLY collaborator info
    // Examples: "(feat. Artist)", "- feat. Artist", "[feat. Artist & Others]"
    if (remainder.match(/^[\s\-–—]*[([]?(?:feat|ft|featuring|with)[\s.]/i)) {
      // It's featuring/collaborator info - but is there ALSO a variant pattern after it?
      // If the remainder has variant patterns (Live, Redux, etc.), it's BOTH feat + variant
      if (isTrackVariant(remainder)) {
        return true; // Has variant pattern, so it IS a variant
      }
      return false; // Just collaborator info, NOT a variant
    }

    // If we stripped the shorter title and the longer one still has variant patterns, it's a variant
    return isTrackVariant(remainder);
  }

  // Strip variant suffixes from both
  const base1 = stripVariantSuffix(title1).toLowerCase().trim();
  const base2 = stripVariantSuffix(title2).toLowerCase().trim();

  // If they're identical after stripping, check if they're really variants
  if (base1 === base2 && base1.length > 0) {
    // Check if BOTH original titles had variant patterns
    const title1HasVariant = isTrackVariant(title1);
    const title2HasVariant = isTrackVariant(title2);

    // Only consider them variants if BOTH have variant patterns
    // This prevents false positives where "Redux" or other ambiguous words are stripped
    if (title1HasVariant && title2HasVariant) {
      return true;
    }

    // If only one or neither has variant patterns, they're NOT variants
    return false;
  }

  return false;
}

/**
 * Get variant type from a title for display purposes
 */
export function getVariantType(title: string): string | null {
  if (!title || typeof title !== "string")
    return null;

  if (/live/i.test(title))
    return "Live";
  if (/acoustic/i.test(title))
    return "Acoustic";
  if (/remix/i.test(title))
    return "Remix";
  if (/\b(?:extended|radio)\s+mix/i.test(title))
    return "Mix";
  if (/remaster/i.test(title))
    return "Remaster";
  if (/instrumental/i.test(title))
    return "Instrumental";
  if (/demo/i.test(title))
    return "Demo";
  if (/unplugged/i.test(title))
    return "Unplugged";
  if (/explicit/i.test(title))
    return "Explicit";
  if (/clean/i.test(title))
    return "Clean";

  return "Variant";
}
